import { DoseTarget } from '~/backend/connector-api/connector-schemas';
import { RoiInterpretedType } from '~/domain/dicom/dicomstructureset';
import { RoiReadModel } from '~/domain/readmodels/StructureSetReadModel';

const PTV_REGEX_PATTERN = /^PTV_(\d+)$/;

export interface DoseTargetData {
    name: string;
    isTarget: boolean;
    doseQuantity: number;
    interpretedType: RoiInterpretedType;
    // Optional because session-payload structures don't carry one. When
    // present, the UI uses it to follow ROI renames.
    roiNumber?: number;
}

const excludedTypes: RoiInterpretedType[] = [
    RoiInterpretedType.EXTERNAL,
    RoiInterpretedType.BOLUS,
    RoiInterpretedType.FIXATION,
    RoiInterpretedType.SUPPORT,
    RoiInterpretedType.AVOIDANCE,
];

const targetOrder: RoiInterpretedType[] = [
    RoiInterpretedType.PTV,
    RoiInterpretedType.CTV,
    RoiInterpretedType.GTV,
];

/**
 * Parses the dose quantity from the target name using a regular expression.
 *
 * @param {string} name - The name of the ROI, e.g., "PTV_46".
 * @returns {number} - The extracted dose in Gy. For doses >= 100, divides by 100.
 *                     E.g., "PTV_4670" -> 46.7 Gy; "PTV_1" -> 1 Gy.
 */
function parseDoseFromName(name: string): number {
    const match = name.match(PTV_REGEX_PATTERN);
    if (!match) {
        return 0;
    }
    const doseValue = parseFloat(match[1]);
    return doseValue >= 100 ? doseValue / 100 : doseValue;
}

/**
 * Builds a name → prescription-dose lookup from a list of dose targets.
 * When `currentNameByRoiNumber` is provided, the resolver's current name
 * is preferred so the lookup follows ROI renames instead of being
 * pinned to the historical prescription stored on `doseDataState.targets`.
 */
export function buildTargetDoseLookup(
    targets: ReadonlyArray<DoseTargetData> | undefined,
    currentNameByRoiNumber?: (roiNumber: number) => string | undefined
): Map<string, number> {
    const lookup = new Map<string, number>();
    if (!targets) {
        return lookup;
    }
    for (const target of targets) {
        if (!target.isTarget) {
            continue;
        }
        const resolvedName =
            target.roiNumber !== undefined && currentNameByRoiNumber
                ? (currentNameByRoiNumber(target.roiNumber) ?? target.name)
                : target.name;
        lookup.set(resolvedName, target.doseQuantity);
    }
    return lookup;
}

/**
 * Transforms a list of ROI structures into dose targets.
 *
 * @param {Array<Roi>} structures - The list of ROI structures.
 * @returns {DoseTargetData[]} - A list of dose targets with relevant information.
 *
 * The function:
 * 1. Filters out excluded ROI types.
 * 2. Maps remaining structures to `DoseTargetData` format.
 * 3. Sorts targets based on their priority (PTV > CTV > GTV).
 */
export default function transformStructuresToDoseTargets(
    structures: ReadonlyArray<RoiReadModel>
): DoseTargetData[] {
    return structures
        .filter(
            (structure) =>
                /*structure.volumeCm3 && TODO: reimplement checking for the structure volume here*/
                !excludedTypes.includes(structure.interpretedType)
        )
        .map((structure) => ({
            name: structure.name,
            isTarget: PTV_REGEX_PATTERN.test(structure.name), // Mark as target if matches the regex
            doseQuantity: parseDoseFromName(structure.name), // Set dose from regex match or 0
            interpretedType: structure.interpretedType,
            roiNumber: structure.roiNumber,
        }))
        .sort((a, b) => {
            const aPriority = targetOrder.indexOf(a.interpretedType);
            const bPriority = targetOrder.indexOf(b.interpretedType);
            if (aPriority !== -1 && bPriority !== -1) {
                return a.name.localeCompare(b.name);
            } else if (aPriority !== -1) {
                return -1;
            } else if (bPriority !== -1) {
                return 1;
            } else {
                return a.name.localeCompare(b.name);
            }
        });
}

/**
 * Converts dose target data into `DoseTarget[]` format for the backend.
 *
 * @param {DoseTargetData[]} targets - Array of dose target data.
 * @returns {DoseTarget[]} - Array of targets with `roi_name` and `dose`.
 */
export const transformDoseTargets = (
    targets: DoseTargetData[]
): DoseTarget[] => {
    return targets
        .filter((target) => target.isTarget)
        .map((target) => ({
            roi_name: target.name,
            dose: target.doseQuantity,
        }));
};

/**
 * Finds the highest dose quantity from the dose target data.
 *
 * @param {DoseTargetData[]} targets - Array of dose target data.
 * @returns {number | null} - The highest dose quantity or null if no targets exist.
 */
export const getHighestDoseQuantity = (
    targets?: DoseTargetData[]
): number | null => {
    if (!targets || targets.length === 0) {
        return null; // Return null if there are no targets
    }

    return targets.reduce((max, target) => {
        return target.doseQuantity > max ? target.doseQuantity : max;
    }, 0);
};
